import mustache from "mustache";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { LOTE_COLORS, loteAreaHectareas, LoteSplitState } from "./LoteSplitState";
import lotesCreadosTemplate from "./paso4LotesCreadosTemplate.html";
import template from "./paso4InstructionsTemplate.html";

mustache.parse(lotesCreadosTemplate);

/** Aside del paso 4 (dividir en lotes). Solo lleva el título y la sección
 *  "Lotes creados" (tabla Lote/Área Has/Nombre, con el nombre editable),
 *  portada 1:1 del control original (MODULO 12, VIEW 4, `#lotListBody` dentro
 *  del aside `#indicationsForAOI_V4`) — el original NO tiene un panel de lotes
 *  aparte junto al mapa, todo vive acá. Foco/blur del input resalta/quita el
 *  resalte de la pieza en el mapa (`onFocusLote`/`onBlurLote`, inyectados por
 *  quien construye este render — `RegistroFincaPaso4Render`). Se re-renderiza
 *  en cada cambio del `LoteSplitState` compartido (mismo estado que usa el
 *  mapa), con sus mismos colores y áreas (`LOTE_COLORS`/`loteAreaHectareas`).
 *
 *  **Sacados por pedido explícito:** la barra de progreso "Paso 4 de 6", el
 *  eyebrow, el subtítulo, la guía numerada de 4 pasos y el botón "Fusionar"
 *  de cada fila.
 *
 *  La lógica de fusión sí se borró, y no por el botón: `fusionar` sólo aceptaba
 *  las dos mitades de un mismo corte, reconocidas por un `loteOrigenId`
 *  compartido, y ese parentesco dejó de existir en el paso 4 cuando el corte
 *  pasó al servidor — `tbl_temp_lotes_finca` no tiene columna de lote de origen.
 *  Quedaba como una segunda implementación de la unión que ya no podía correr.
 *  Si vuelve el botón, el parentesco está en `mitadesIds` de `dividirLoteAsync`
 *  y la unión en el rpc `UnirLotes`. */
export class Paso4InstructionsRender implements IConnectRender {
  private _lotesEl: HTMLElement | null = null;

  constructor(
    private readonly _state: LoteSplitState,
    private readonly _onFocusLote: (id: number) => void,
    private readonly _onBlurLote: () => void,
  ) {}

  render(): string {
    return template;
  }

  connect(): void {
    this._lotesEl = document.getElementById("paso4LotesCreados");
    this._state.addOnChangeListener(this._refresh);
    this._refresh();
  }

  /** Lleva el sidebar hasta el input de nombre de un lote y le da el foco.
   *
   *  **Es el camino inverso al que ya existía.** El foco del input resaltaba el
   *  lote en el mapa (`_onFocusLote`); esto hace que clickear el lote en el mapa
   *  traiga su input a la vista. Con una finca de muchos lotes la tabla no entra
   *  en pantalla, así que sin el scroll el input quedaba enfocado sin que se
   *  viera cuál.
   *
   *  No se pinta el resalte desde acá: darle el foco al input dispara su propio
   *  `focus`, que es el que ya llama a `setHighlighted`. Un solo camino para esa
   *  decisión.
   *
   *  `block: "nearest"` y no `"center"`: si el input ya está a la vista, no se
   *  mueve nada. Centrarlo haría saltar la tabla en cada click, también cuando
   *  no hacía falta. */
  enfocarInputDe(loteId: number): void {
    const input = this._lotesEl?.querySelector<HTMLInputElement>(
      `.paso4-lotes-creados-name-input[data-lote-id="${loteId}"]`,
    );
    if (!input) return;

    input.scrollIntoView({ behavior: "smooth", block: "nearest" });
    // `preventScroll`: `focus()` por su cuenta también lleva el elemento a la
    // vista, de golpe, y eso le pelea el scroll suave que acaba de arrancar la
    // línea de arriba — se veía como un salto y después la animación.
    input.focus({ preventScroll: true });
    // El cursor al final y nada seleccionado: el usuario viene de clickear en el
    // mapa, no de querer reemplazar el nombre. Con `select()` el primer tecleo
    // le borraría lo que había.
    input.setSelectionRange(input.value.length, input.value.length);
  }

  dispose(): void {}

  /** Ids de los lotes tal como están pintados ahora, en orden. Es lo que decide
   *  si alcanza con actualizar en sitio o hay que rehacer la tabla. */
  private _idsPintados: number[] = [];

  private _refresh = (): void => {
    if (!this._lotesEl) return;

    const filas = this._state.getAll().map((entry, idx) => ({
      id: entry.id,
      color: LOTE_COLORS[idx % LOTE_COLORS.length],
      nombreLote: entry.nombreLote,
      areaHa: loteAreaHectareas(entry.polygon),
    }));

    // **Sin altas ni bajas, la tabla NO se rehace.**
    //
    // Rehacerla siempre tenía un costo que no se veía venir: el `change` de un
    // input dispara `rename`, el estado avisa, y este refresco llegaba ANTES del
    // `click` del input al que el usuario estaba yendo. Ese input ya no era el
    // mismo nodo —`innerHTML` lo había reemplazado—, así que el click se perdía y
    // había que dar un segundo para entrar a editar. Pasaba con cualquier foco
    // que viniera de un blur con cambio: escribir un nombre y clickear otro
    // campo.
    //
    // De paso, un refresco en sitio no le pisa el valor al input que tiene el
    // foco ni le mueve el caret, que es la otra cosa que un `innerHTML` arruina.
    const mismaEstructura =
      filas.length === this._idsPintados.length &&
      filas.every((fila, i) => fila.id === this._idsPintados[i]);

    if (mismaEstructura) {
      this._actualizarEnSitio(filas);
      return;
    }

    this._lotesEl.innerHTML = mustache.render(lotesCreadosTemplate, {
      count: filas.length,
      lotes: filas,
    });
    this._idsPintados = filas.map((fila) => fila.id);

    this._connectInputs();
  };

  /** Escribe los valores sobre las filas que YA están en el DOM, sin tocar su
   *  estructura ni volver a enganchar listeners.
   *
   *  El input del lote enfocado se deja como está: es el que el usuario está
   *  tecleando, y su valor ya viajó al estado por `rename` — reasignarlo sólo
   *  podría perderle el caret. */
  private _actualizarEnSitio(
    filas: readonly {
      id: number;
      color: string;
      nombreLote: string;
      areaHa: string | number;
    }[],
  ): void {
    for (const fila of filas) {
      const input = this._lotesEl?.querySelector<HTMLInputElement>(
        `.paso4-lotes-creados-name-input[data-lote-id="${fila.id}"]`,
      );
      const celdas = input?.closest("tr")?.children;
      if (!input || !celdas) continue;

      const badge = celdas[0]?.querySelector<HTMLElement>(".sx-badge");
      if (badge) badge.style.background = fila.color;

      const area = String(fila.areaHa);
      if (celdas[1] && celdas[1].textContent !== area) {
        celdas[1].textContent = area;
      }

      if (document.activeElement !== input && input.value !== fila.nombreLote) {
        input.value = fila.nombreLote;
      }
    }
  }

  private _connectInputs(): void {
    this._lotesEl
      ?.querySelectorAll<HTMLInputElement>(".paso4-lotes-creados-name-input")
      .forEach((input) => {
        const id = Number(input.dataset.loteId);
        input.addEventListener("focus", () => this._onFocusLote(id));
        input.addEventListener("blur", () => this._onBlurLote());
        // **El nombre se guarda tal como se escribe (2026-10-09).** Acá había
        // un listener de `input` que lo pasaba a mayúsculas tecla por tecla
        // —con todo el trabajo de reponer el caret, porque reasignar `value`
        // lo manda al final— y otro `toUpperCase()` en el `change`. Se quitó a
        // pedido: el usuario escribe "Lote 71" y eso es lo que ve.
        //
        // ⚠️ El guardado final SÍ sigue guardando el nombre en mayúsculas
        // (`PersistirFincaAsync`, del lado del servidor), así que en la finca
        // terminada el lote aparece como "LOTE 71". Si eso también tiene que
        // cambiar, es ahí y no acá.
        input.addEventListener("change", () => {
          this._state.rename(id, input.value);
        });
      });
  }
}
